import { z } from 'zod';
import { Users } from '../db-entries/user.js';
import { CustomProfileFieldType, customProfileFieldTypeGuard, fieldPartGuard, userProfileAddressKeys, userProfileGuard, } from '../foundations/index.js';
import { ossUserOnboardingDataKey, userOnboardingDataKey } from './onboarding.js';
import { defaultTenantIdKey } from './tenant.js';
import { consoleUserPreferenceKey, guideRequestsKey } from './user.js';
const baseProfileFieldGuard = z.object({
    name: z.string(),
    type: customProfileFieldTypeGuard,
    label: z.string().min(1).optional(),
    description: z.string().optional(),
    required: z.boolean(),
});
export const textProfileFieldGuard = baseProfileFieldGuard.extend({
    type: z.literal(CustomProfileFieldType.Text),
    config: z
        .object({
        placeholder: z.string().optional(),
        minLength: z.number().optional(),
        maxLength: z.number().optional(),
    })
        .optional(),
});
export const numberProfileFieldGuard = baseProfileFieldGuard.extend({
    type: z.literal(CustomProfileFieldType.Number),
    config: z
        .object({
        placeholder: z.string().optional(),
        minValue: z.number().optional(),
        maxValue: z.number().optional(),
    })
        .optional(),
});
export const dateProfileFieldGuard = baseProfileFieldGuard.extend({
    type: z.literal(CustomProfileFieldType.Date),
    config: z
        .object({
        placeholder: z.string().optional(),
        format: z.string(),
        customFormat: z.string().optional(),
    })
        .optional(),
});
export const checkboxProfileFieldGuard = baseProfileFieldGuard.omit({ description: true }).extend({
    type: z.literal(CustomProfileFieldType.Checkbox),
    required: z.literal(false),
    config: z
        .object({
        defaultValue: z.literal('true').or(z.literal('false')),
    })
        .optional(),
});
export const selectProfileFieldGuard = baseProfileFieldGuard.extend({
    type: z.literal(CustomProfileFieldType.Select),
    config: z.object({
        placeholder: z.string().optional(),
        options: z.array(z.object({ label: z.string().optional(), value: z.string() })),
    }),
});
export const urlProfileFieldGuard = baseProfileFieldGuard.extend({
    type: z.literal(CustomProfileFieldType.Url),
    config: z
        .object({
        placeholder: z.string().optional(),
    })
        .optional(),
});
export const regexProfileFieldGuard = baseProfileFieldGuard.extend({
    type: z.literal(CustomProfileFieldType.Regex),
    config: z.object({
        placeholder: z.string().optional(),
        format: z.string(),
    }),
});
export const addressProfileFieldGuard = baseProfileFieldGuard.extend({
    type: z.literal(CustomProfileFieldType.Address),
    config: z.object({
        parts: z.array(fieldPartGuard.omit({ name: true }).extend({
            name: z.enum(userProfileAddressKeys),
        })),
    }),
});
export const fullnameKeys = userProfileGuard
    .pick({
    givenName: true,
    middleName: true,
    familyName: true,
})
    .keyof().options;
export const fullnameProfileFieldGuard = baseProfileFieldGuard.extend({
    type: z.literal(CustomProfileFieldType.Fullname),
    config: z.object({
        parts: z.array(fieldPartGuard.omit({ name: true }).extend({
            name: z.enum(fullnameKeys),
        })),
    }),
});
export const customProfileFieldUnionGuard = z.discriminatedUnion('type', [
    textProfileFieldGuard,
    numberProfileFieldGuard,
    dateProfileFieldGuard,
    checkboxProfileFieldGuard,
    selectProfileFieldGuard,
    urlProfileFieldGuard,
    regexProfileFieldGuard,
    addressProfileFieldGuard,
    fullnameProfileFieldGuard,
]);
export const nameAndAvatarGuard = z
    .object({
    name: z.string(),
    avatar: z.string().url().or(z.literal('')),
})
    .partial();
export const builtInProfileGuard = nameAndAvatarGuard.merge(z.object({ profile: userProfileGuard }));
export const builtInCustomProfileFieldKeys = Object.freeze(builtInProfileGuard.merge(userProfileGuard).keyof().options);
export const updateCustomProfileFieldDataGuard = z.discriminatedUnion('type', [
    textProfileFieldGuard.omit({ name: true }),
    numberProfileFieldGuard.omit({ name: true }),
    dateProfileFieldGuard.omit({ name: true }),
    checkboxProfileFieldGuard.omit({ name: true }),
    selectProfileFieldGuard.omit({ name: true }),
    urlProfileFieldGuard.omit({ name: true }),
    regexProfileFieldGuard.omit({ name: true }),
    addressProfileFieldGuard.omit({ name: true }),
    fullnameProfileFieldGuard.omit({ name: true }),
]);
export const updateCustomProfileFieldSieOrderGuard = z.object({
    name: z.string(),
    sieOrder: z.number(),
});
/** Reserved custom data keys, which are used by the system and should not be used by custom profile fields. */
export const reservedCustomDataKeyGuard = z
    .object({
    [userOnboardingDataKey]: z.string(),
    [ossUserOnboardingDataKey]: z.string(),
    [guideRequestsKey]: z.string(),
    [consoleUserPreferenceKey]: z.string(),
    [defaultTenantIdKey]: z.string(),
})
    .partial();
export const reservedCustomDataKeys = Object.freeze(reservedCustomDataKeyGuard.keyof().options);
/** Disallow sign-in identifier related field keys in custom profile fields to avoid conflicts with built-in sign-in/sign-up flows. */
export const signInIdentifierKeyGuard = Users.createGuard
    .pick({
    username: true,
    primaryEmail: true,
    primaryPhone: true,
})
    .extend({
    email: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
});
export const reservedSignInIdentifierKeys = Object.freeze(signInIdentifierKeyGuard.keyof().options);
/** Reserved user profile keys. Currently only `preferredUsername` is reserved for standard IdP usage. */
export const reservedBuiltInProfileKeyGuard = userProfileGuard.pick({ preferredUsername: true });
export const reservedBuiltInProfileKeys = Object.freeze(reservedBuiltInProfileKeyGuard.keyof().options);
export var SupportedDateFormat;
(function (SupportedDateFormat) {
    SupportedDateFormat["US"] = "MM/dd/yyyy";
    SupportedDateFormat["UK"] = "dd/MM/yyyy";
    SupportedDateFormat["ISO"] = "yyyy-MM-dd";
    SupportedDateFormat["Custom"] = "custom";
})(SupportedDateFormat || (SupportedDateFormat = {}));
export var Gender;
(function (Gender) {
    Gender["Female"] = "female";
    Gender["Male"] = "male";
    Gender["Other"] = "prefer_not_to_say";
})(Gender || (Gender = {}));
/* eslint-enable max-lines */
